// SPDX-License-Identifier: MIT
import React from 'react';
import type { FC } from 'react';
import { useTheme } from 'styled-components';
import { validateAzureConfig } from './utils/config-validation';
import { authenticateWithSas, logoutAzure, redactSas } from './auth/sas-auth-strategy';
import { isEncryptedSasToken } from './utils/crypto';
import { getSessionInfo } from './auth/session';
import { AzureBrowserPane } from './components/AzureBrowserPane';
import { useState, useCallback, useMemo } from 'react';
import { getSasValidationMessage, validateSasInput } from './utils/sas-validation';

// Icon switched from inline SVG to branded image asset for consistency with other provider cards.
// Served from public/assets; Vite copies this as-is. Using role="img" with descriptive alt text for a11y.
export const AzureIcon: FC = () => (
  <img
    src={`${import.meta.env.VITE_PUBLIC_URL || ''}/assets/azure-storage-blob.png`}
    width={48}
    height={48}
    style={{ display: 'block' }}
    alt='Azure Blob Storage'
    loading='lazy'
    decoding='async'
  />
);

export const AzureProviderInterface: FC = () => {
  const { valid: configValid, issues, config } = validateAzureConfig();
  const getEnv = (k: string): string => {
    // import.meta.env has string | boolean | undefined values depending on Vite typing
    // Narrow safely to string for our known keys
    const v = (import.meta as unknown as { env: Record<string, unknown> }).env[k];
    return typeof v === 'string' ? v : '';
  };
  // Prefer validated config values (which tests may mock), falling back to env
  const defaultAccount = config.accountName || getEnv('VITE_AZURE_ACCOUNT_NAME');
  const defaultContainer = config.containerName || getEnv('VITE_AZURE_CONTAINER_NAME');
  const [accountName, setAccountName] = useState(defaultAccount);
  const [containerName, setContainerName] = useState(defaultContainer);
  const [sasInput, setSasInput] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [authState, setAuthState] = useState<'idle' | 'authenticating' | 'authenticated' | 'error'>(
    'idle'
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const sessionInfo = getSessionInfo();

  // Build effective SAS (accept full URL or raw token)
  const effectiveSas = React.useMemo(() => {
    const raw = sasInput.trim();
    if (!raw) return '';
    // If user pasted an encrypted dev token, pass it through unchanged so decrypt path can handle it
    if (isEncryptedSasToken(raw)) return raw;
    if (/^https?:\/\//i.test(raw)) return raw; // already full URL
    if (accountName && containerName) {
      return `https://${accountName}.blob.core.windows.net/${containerName}?${raw}`;
    }
    return raw;
  }, [sasInput, accountName, containerName]);

  const { isValid: isSasValid, reason: sasValidationReason } = useMemo(
    () => validateSasInput(sasInput, { accountName, containerName }),
    [sasInput, accountName, containerName]
  );

  const showSasValidation = sasInput.trim().length > 0 && !isSasValid;
  const sasValidationMessage = showSasValidation
    ? getSasValidationMessage(sasValidationReason)
    : '';
  const canAuthenticate = Boolean(effectiveSas) && configValid && isSasValid;

  const onAuthenticate = useCallback(async () => {
    setAuthState('authenticating');
    setErrorMsg(null);
    const res = await authenticateWithSas(effectiveSas);
    if (res.success) {
      setAuthState('authenticated');
    } else {
      setAuthState('error');
      setErrorMsg(res.error || 'Authentication failed');
    }
  }, [effectiveSas]);

  const onLogout = useCallback(() => {
    logoutAzure();
    setAuthState('idle');
    setErrorMsg(null);
  }, []);

  const theme = useTheme(); // strongly typed DefaultTheme
  // Cleanup credentials on unmount (defense-in-depth vs lingering session in memory)
  React.useEffect(
    () => () => {
      logoutAzure();
    },
    []
  );
  return (
    <div
      style={{
        padding: '1.25rem 1.5rem',
        background: theme?.panelBackground || '#0d1117',
        color: theme?.textColor || '#e6edf3',
        borderRadius: 8,
        border: `1px solid ${theme?.borderColor || '#30363d'}`,
        boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}
      data-testid='azure-provider-card'
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
        <AzureIcon />
        <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, color: theme?.textColor }}>
          LoadSEER Storage
        </h3>
      </div>
      {!configValid && (
        <div style={{ marginBottom: '0.75rem', fontSize: '0.75rem', color: '#ffa657' }}>
          <strong>Configuration Issues:</strong>
          <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0 }}>
            {issues.map(i => (
              <li key={i.field}>
                {i.field}: {i.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {configValid && authState !== 'authenticated' && (
        <div
          style={{ marginBottom: '0.75rem', fontSize: '0.75rem', color: '#3fb950' }}
          data-testid='azure-config-valid'
        >
          Configuration valid.
        </div>
      )}
      {authState !== 'authenticated' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: '0.65rem', fontWeight: 600 }}>Account</label>
              <input
                data-testid='azure-account-input'
                placeholder='account name'
                value={accountName}
                onChange={e => setAccountName(e.target.value.trim())}
                style={{
                  width: '100%',
                  background: theme?.inputBgd,
                  color: theme?.inputColor,
                  border: `1px solid ${theme?.inputBorderColor}`,
                  borderRadius: 4,
                  padding: '4px 6px',
                  fontSize: 12,
                }}
              />
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: '0.65rem', fontWeight: 600 }}>Container</label>
              <input
                data-testid='azure-container-input'
                placeholder='container name'
                value={containerName}
                onChange={e => setContainerName(e.target.value.trim())}
                style={{
                  width: '100%',
                  background: theme?.inputBgd,
                  color: theme?.inputColor,
                  border: `1px solid ${theme?.inputBorderColor}`,
                  borderRadius: 4,
                  padding: '4px 6px',
                  fontSize: 12,
                }}
              />
            </div>
          </div>
          <label
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: theme?.labelColor || theme?.textColor,
            }}
          >
            SAS Token or URL
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              data-testid='azure-sas-input'
              type={showToken ? 'text' : 'password'}
              style={{
                flex: 1,
                background: theme?.inputBgd || '#161b22',
                color: theme?.inputColor || theme?.textColor,
                border: `1px solid ${theme?.inputBorderColor || '#30363d'}`,
                borderRadius: 4,
                fontFamily: 'monospace',
                fontSize: 12,
                padding: '6px 8px',
              }}
              placeholder='Paste container SAS URL or raw token'
              value={sasInput}
              onChange={e => setSasInput(e.target.value)}
              autoComplete='off'
            />
            <button
              type='button'
              onClick={() => setShowToken(s => !s)}
              style={{
                background: theme?.secondaryBtnBgd,
                color: theme?.secondaryBtnColor,
                border: `1px solid ${theme?.borderColor}`,
                padding: '6px 10px',
                fontSize: 11,
                borderRadius: 4,
                cursor: 'pointer',
              }}
              data-testid='azure-token-visibility-btn'
            >
              {showToken ? 'Hide' : 'Show'}
            </button>
          </div>
          <div style={{ fontSize: '0.6rem', opacity: 0.6 }}>
            {accountName && containerName
              ? `Will auth: https://${accountName}.blob.core.windows.net/${containerName}`
              : 'Enter account & container for raw token.'}
          </div>
          {showSasValidation && (
            <div
              style={{ color: '#ffa657', fontSize: '0.7rem' }}
              role='status'
              data-testid='azure-sas-validation'
            >
              {sasValidationMessage}
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              data-testid='azure-auth-button'
              onClick={onAuthenticate}
              disabled={!canAuthenticate || authState === 'authenticating'}
              style={{
                background: theme?.primaryBtnBgd,
                color: theme?.primaryBtnColor,
                border: `1px solid ${theme?.primaryBtnBgdHover || theme?.primaryBtnBgd}`,
                padding: '6px 12px',
                fontSize: 12,
                borderRadius: 4,
                cursor: !canAuthenticate ? 'not-allowed' : 'pointer',
                opacity: !canAuthenticate ? 0.5 : 1,
              }}
            >
              {authState === 'authenticating' ? 'Connecting…' : 'Connect'}
            </button>
            <button
              type='button'
              onClick={() => setSasInput('')}
              disabled={authState === 'authenticating'}
              style={{
                background: theme?.secondaryBtnBgd,
                color: theme?.secondaryBtnColor,
                border: `1px solid ${theme?.borderColor}`,
                padding: '6px 12px',
                fontSize: 12,
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          </div>
          {errorMsg && (
            <div style={{ color: '#ffa657', fontSize: '0.7rem' }} data-testid='azure-auth-error'>
              Error: {errorMsg}
            </div>
          )}
        </div>
      )}
      {authState === 'authenticated' && (
        <div
          style={{ fontSize: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
          data-testid='azure-auth-success'
        >
          <div style={{ color: theme?.successColor || '#3fb950' }}>Connected.</div>
          {sessionInfo.expiresAt && <div>Expires in ~{sessionInfo.minutesRemaining}m</div>}
          {sessionInfo.permissions && <div>Permissions: {sessionInfo.permissions}</div>}
          <button
            onClick={onLogout}
            style={{
              alignSelf: 'flex-start',
              background: theme?.negativeBtnBgd || '#d92c43',
              color: theme?.primaryBtnColor || '#fff',
              border: `1px solid ${theme?.negativeBtnBgdHover || '#ff4d5b'}`,
              padding: '4px 10px',
              fontSize: 12,
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Disconnect
          </button>
          <div style={{ fontSize: '0.65rem', opacity: 0.65 }}>
            SAS:{' '}
            {(() => {
              if (!effectiveSas) return '—';
              const r = redactSas(effectiveSas);
              return r.length > 14 ? `${r.slice(0, 6)}…${r.slice(-4)}` : '…redacted…';
            })()}
          </div>
          <AzureBrowserPane />
        </div>
      )}
    </div>
  );
};
